import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';
import { parseISO, differenceInDays, format } from 'date-fns';
import { formatDate } from 'helpers/intl-helper';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';

import { assetsByDate } from 'helpers/asset-utils';
import Common from 'helpers/intl-common';
import CCCheckbox from 'components/shared/DesignSystem/SlabTransferPortal/CCCheckbox';
import RangeInput from 'components/shared/DesignSystem/SlabTransferPortal/RangeInput';
import ScreenReaderContent from 'components/shared/DesignSystem/shared/components/ScreenReaderContent';
import Spinner from 'components/shared/DesignSystem/Spinner';
import { useMultiselect } from 'components/shared/forms/MultiselectPicker';
import EmptyStateImage from 'images/RickThumbsUp.png';
import { useTrashAssets } from './hooks/useTrashAssets';
import Asset from './Asset';
import TrashCanMessage from './TrashCanMessage';
import { Button } from '@companycam/slab-web';

const initialThumbnailSize = 140;

// #region styled-components
const Container = styled.div``;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--cc_size_spacing_s);
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 2;
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_xl);
  min-height: 72px;
  background: var(--cc_color_background_1);
`;
const ActionsPrimary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--cc_size_spacing_xxl);
  align-items: center;
`;
const Sections = styled.div`
  padding: 0 0 var(--cc_size_spacing_xl);
`;
const SectionHeader = styled.div`
  padding: var(--cc_size_spacing_l) var(--cc_size_spacing_xl)
    var(--cc_size_spacing_xs);
`;
const SectionBody = styled.div`
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_xl)
    var(--cc_size_spacing_xs) 58px;
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(props) => props.thumbnailSize}px, 1fr)
  );
  column-gap: var(--cc_size_spacing_s);
  row-gap: var(--cc_size_spacing_l);
`;
const CheckboxLabelText = styled.span`
  font-family: 'Averta', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: var(--cc_size_text_m);
  color: var(--cc_color_text_default);
`;
const MessageAlertIcon = styled.i`
  font-size: var(--cc_size_text_xxxl);
  color: var(--cc_color_utility_destroy);
`;

const restoreButtonEnter = keyframes`
  50% {
    opacity: 1;
    transform: translate3d(-2%, 0, 0);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;
const RestoreButton = styled(Button)`
  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translate3d(4%, 0, 0);
    animation-name: ${restoreButtonEnter};
    animation-duration: 0.4s;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
  }
`;
const ThumbnailSize = styled.div`
  width: 175px;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: var(--cc_size_spacing_s);
`;
const ThumbnailSizeIcon = styled.i`
  color: var(--cc_color_text_nonessential);
  font-size: 20px;
`;
// #endregion

const messages = defineMessages({
  emptyHeading: {
    id: 'Project.trashCan.emptyHeading',
    defaultMessage: 'Nothing to See Here',
    description: 'Project trash can empty heading',
  },
  emptyMessage: {
    id: 'Project.trashCan.emptyMessage',
    defaultMessage: 'No photos are currently in the Trash.',
    description: 'Project trash can empty state message',
  },
  errorMessage: {
    id: 'Project.trashCan.errorMessage',
    defaultMessage: 'There was an error loading the Trash. Probably our fault!',
    description: 'Project trash can error message',
  },
  restoreAssetsLoading: {
    id: 'Project.trashCan.restoreAssetsLoading',
    defaultMessage: 'Moving Photos back to project...',
    description: 'Project trash can restore assets loading message',
  },
});

const DeletedAssets = ({ projectId }) => {
  const [thumbnailSize, setThumbnailSize] = useState(initialThumbnailSize);
  const handleThumbnailSizeChange = (e) => {
    setThumbnailSize(e.target.value);
  };
  const {
    fetchDeletedAssets,
    fetchDeletedAssetsLoading,
    restoreDeletedAssetsLoading,
    error,
    deletedAssets,
    restoreDeletedAssets,
    IMAGE_TYPE,
    VIDEO_TYPE,
  } = useTrashAssets({
    projectId,
  });
  const intl = useIntl();
  const empty = !fetchDeletedAssetsLoading && !deletedAssets.length;

  useEffect(() => {
    fetchDeletedAssets();
  }, [fetchDeletedAssets, projectId]);

  const SECTION_DATE_FORMAT = 'PPPP';
  const assetSections = useMemo(
    () =>
      assetsByDate({
        assets: deletedAssets,
        sectionDateFormat: SECTION_DATE_FORMAT,
      }),
    [deletedAssets],
  );
  const multiselectProps = useMultiselect({ selectableItems: deletedAssets });
  const {
    getAllSelected,
    selectAll,
    clearSelection,
    checkSelected,
    checkGroupSelected,
    toggleSelected,
    toggleGroupSelected,
  } = multiselectProps;

  const renderAssetSection = (section) => (
    <section key={section.key}>
      <SectionHeader>
        <CCCheckbox
          id={`deleted-assets__section-${section.key}`}
          data-testid={`deleted-assets__section-${section.key}`}
          label={<CheckboxLabelText>{section.title}</CheckboxLabelText>}
          shape="circle"
          size={20}
          onChange={() => toggleGroupSelected(section.assets)}
          checked={checkGroupSelected(section.assets)}
          style={{ display: 'flex' }}
          checkedBackgroundColor="var(--cc_color_button_background_primary)"
          checkmarkColor="var(--cc_color_button_text_primary)"
        />
      </SectionHeader>
      <SectionBody thumbnailSize={thumbnailSize}>
        {section.assets.map((asset) => {
          const daysToPurge = differenceInDays(
            parseISO(asset.purge_at),
            Date.now(),
          );
          const imageUrl =
            thumbnailSize > 250 ? asset.url_large : asset.url_medium;
          return (
            <Asset
              key={asset.id}
              isVideo={asset.type === VIDEO_TYPE}
              url={
                asset.type === IMAGE_TYPE
                  ? imageUrl
                  : asset.asset_preview_thumbnail
              }
              id={`deleted-assets__asset-${asset.id}`}
              label={`Photo by ${asset.creator} on ${format(
                parseISO(asset.captured_at),
                SECTION_DATE_FORMAT,
              )}`}
              onChange={() => toggleSelected(asset)}
              checked={checkSelected(asset)}
              daysToPurge={asset.purge_at ? daysToPurge : null}
              caption={`${formatDate(parseISO(asset.captured_at), 'p')} • ${
                asset.creator || ''
              }`}
              size={thumbnailSize}
            />
          );
        })}
      </SectionBody>
    </section>
  );

  if (restoreDeletedAssetsLoading) {
    return (
      <TrashCanMessage
        contentBefore={
          <Spinner
            size="large"
            label={intl.formatMessage(messages.restoreAssetsLoading)}
          />
        }
      />
    );
  }

  if (fetchDeletedAssetsLoading) {
    return (
      <TrashCanMessage
        contentBefore={
          <Spinner size="large" label={intl.formatMessage(Common.loading)} />
        }
      />
    );
  }

  if (error) {
    return (
      <TrashCanMessage
        heading={intl.formatMessage(Common.error)}
        message={intl.formatMessage(messages.errorMessage)}
        contentBefore={
          <MessageAlertIcon
            className="mdi mdi-alert-circle"
            aria-hidden="true"
          />
        }
      />
    );
  }

  if (empty) {
    return (
      <TrashCanMessage
        contentBefore={
          <img src={EmptyStateImage} alt="" width="115" height="136" />
        }
        heading={`${intl.formatMessage(messages.emptyHeading)}...`}
        message={intl.formatMessage(messages.emptyMessage)}
      />
    );
  }

  return (
    <Container>
      <Actions>
        <ActionsPrimary>
          <CCCheckbox
            id="deleted-assets__select-all"
            data-testid="deleted-assets__select-all"
            label={
              <CheckboxLabelText>
                <FormattedMessage
                  id="Project.trashCan.selectAll"
                  defaultMessage="Select All"
                  description="checkbox label for selecting all deleted assets"
                />
              </CheckboxLabelText>
            }
            shape="circle"
            size={20}
            onChange={() =>
              checkGroupSelected(deletedAssets) ? clearSelection() : selectAll()
            }
            checked={checkGroupSelected(deletedAssets)}
            style={{ display: 'flex' }}
            checkedBackgroundColor="var(--cc_color_button_background_primary)"
            checkmarkColor="var(--cc_color_button_text_primary)"
          />
          <ThumbnailSize>
            <RangeInput
              id="thumbnail-size-input"
              onChange={handleThumbnailSizeChange}
              value={thumbnailSize}
              label={
                <ScreenReaderContent>
                  <FormattedMessage
                    id="Project.trashCan.changePreviewSize"
                    defaultMessage="Change photo preview size"
                    description="screenreader description for thumbnail size input"
                  />
                </ScreenReaderContent>
              }
              min={initialThumbnailSize}
              max={500}
            />
            <ThumbnailSizeIcon
              aria-hidden="true"
              className="mdi mdi-magnify-plus-outline"
            />
          </ThumbnailSize>
        </ActionsPrimary>

        {getAllSelected().length ? (
          <RestoreButton
            color="primary"
            icon={{
              name: 'undo-variant',
              position: 'before',
            }}
            id="deleted-assets__move-back-to-project"
            data-testid="deleted-assets__move-back-to-project"
            onClick={() => {
              const assetsToRestore = getAllSelected();
              restoreDeletedAssets(assetsToRestore);

              Steven.elementClicked({
                id: 'projects_trash_can_modal_restore',
                content: 'admin user restore project trash',
                type: ELEMENT_TYPES.BUTTON,
                action: 'admin_user_restore_project_trash',
              });
            }}
          >
            <FormattedMessage
              id="Project.trashCan.moveBackToProject"
              defaultMessage="Move Back to Project"
              description="action button label for moving deleted assets back to project"
            />
          </RestoreButton>
        ) : null}
      </Actions>
      <Sections>
        {assetSections.map((section) => renderAssetSection(section))}
      </Sections>
    </Container>
  );
};

DeletedAssets.propTypes = {
  projectId: PropTypes.number,
};

export default DeletedAssets;
